/**
 * Browser-only stand-in for the PocketBase SDK, used by the DEMO build
 * (VITE_DEMO=1). It implements just the surface the app calls — authStore,
 * collection().getFullList/getOne/create/update/delete/authWithPassword — over
 * an in-memory store seeded with the sample project and persisted to
 * localStorage. No network, no backend.
 */

type Rec = Record<string, any>;
type DB = Record<string, Rec[]>;

const LS_KEY = "ph.demo.db.v1";

const FIELD_TO_COLLECTION: Record<string, string> = {
  phase: "phases",
  project: "projects",
  item: "items",
  user: "users",
};

function uid(prefix = "r"): string {
  return prefix + Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
}
function now(): string {
  return new Date().toISOString().replace("T", " ").slice(0, 19) + "Z";
}
const iso = (d: Date) => d.toISOString().slice(0, 10);
const today = () => iso(new Date());
const ago = (n: number) => iso(new Date(Date.now() - n * 86400000));
const soon = (n: number) => iso(new Date(Date.now() + n * 86400000));

function seed(): DB {
  const user = { id: "u1", email: "demo@projecthub.local", name: "Demo Consultant", theme: "sage", mode: "light", digestDay: 1, digestEnabled: true, created: now(), updated: now() };

  const folders: Rec[] = [
    { id: "f1", user: "u1", name: "Healthcare clients", color: "#1F5D50", position: 0, created: now(), updated: now() },
    { id: "f2", user: "u1", name: "Public sector", color: "#8A4B2A", position: 1, created: now(), updated: now() },
  ];

  const projects: Rec[] = [
    { id: "grp1", user: "u1", folderId: "f1", name: "Riverside Health System", client: "Riverside Health System", description: "Umbrella for all Riverside engagements this year.", color: "#1F5D50", status: "active", taskListName: "Riverside", scope: "", created: now(), updated: now() },
    { id: "p1", user: "u1", parent: "grp1", name: "Strategic Plan Refresh", client: "Riverside Health System", description: "Three-year plan refresh for Riverside Health System — discovery, stakeholder engagement and board adoption.", color: "#1F5D50", status: "active", start: "2026-08-03", due: "2026-10-23", taskListName: "Strategic Plan Refresh", scope: "Refresh the three-year strategic plan: environmental scan, stakeholder engagement, priority setting, and board adoption.", fee: "$62,000", contract: "SOW-2026-014", billingType: "fixed", feeAmount: 62000, budgetHours: 320, created: now(), updated: now() },
    { id: "p1b", user: "u1", parent: "grp1", name: "Community Health Needs Assessment", client: "Riverside Health System", description: "Triennial CHNA with community survey and prioritization.", color: "#39707A", status: "planning", due: soon(80), taskListName: "CHNA", scope: "", created: now(), updated: now() },
    { id: "p2", user: "u1", folderId: "f2", billingType: "hourly", name: "Operations Assessment", client: "Cedar County", description: "Cross-department operations review for Cedar County with costed recommendations.", color: "#8A4B2A", status: "active", start: "2026-07-20", due: soon(38), taskListName: "Ops Assessment", scope: "", created: now(), updated: now() },
    { id: "p3", user: "u1", billingType: "retainer", retainerHours: 20, feeAmount: 4000, name: "Brand Positioning Sprint", client: "Northwind Clinic", description: "Six-week positioning and messaging sprint ahead of a service-line launch.", color: "#4A4E69", status: "planning", due: soon(60), taskListName: "Brand Sprint", scope: "", created: now(), updated: now() },
    { id: "inbox1", user: "u1", name: "Inbox", client: "", description: "", color: "#4A4E69", status: "active", kind: "inbox", taskListName: "Inbox", scope: "", created: now(), updated: now() },
  ];

  const phases: Rec[] = [
    { id: "ph1", project: "p1", name: "Discovery", description: "Baseline the current plan and gather the data we need before engaging stakeholders.", color: "#1F5D50", start: "2026-08-03", end: "2026-08-21", position: 0, created: now(), updated: now() },
    { id: "ph2", project: "p1", name: "Stakeholder Engagement", color: "#1F5D50", start: "2026-08-17", end: "2026-09-11", position: 1, created: now(), updated: now() },
    { id: "ph3", project: "p1", name: "Plan Development", color: "#1F5D50", start: "2026-09-07", end: "2026-10-16", position: 2, created: now(), updated: now() },
    { id: "ph4", project: "p2", name: "Data Gathering", color: "#8A4B2A", start: "2026-07-20", end: "2026-08-15", position: 0, created: now(), updated: now() },
    { id: "ph5", project: "p2", name: "Analysis", color: "#8A4B2A", start: "2026-08-11", end: "2026-09-05", position: 1, created: now(), updated: now() },
    { id: "inboxph", project: "inbox1", name: "Inbox", color: "#4A4E69", position: 0, created: now(), updated: now() },
  ];

  const I = (o: Rec): Rec => ({ notes: "", position: 0, syncEnabled: false, clientVisible: false, recur: "none", dependsOn: "", milestone: "", googleTaskId: "", start: "", created: now(), updated: now(), ...o });
  const items: Rec[] = [
    I({ id: "i1", phase: "ph1", title: "Compile prior plan and KPI baseline", type: "task", status: "done", priority: "medium", due: ago(11), syncEnabled: true, notes: "Pulled last plan and the KPI dashboard export from Drive.", position: 0, milestone: "i7" }),
    I({ id: "i2", phase: "ph1", title: "Weekly client check-in call", type: "task", status: "doing", priority: "medium", due: today(), syncEnabled: true, recur: "weekly", position: 1 }),
    I({ id: "i3", phase: "ph1", title: "Service-line volume trend analysis", type: "task", status: "doing", priority: "high", due: today(), syncEnabled: true, position: 2, milestone: "i7" }),
    I({ id: "i4", phase: "ph1", title: "Discovery findings memo", type: "deliverable", status: "todo", priority: "high", due: soon(3), clientVisible: true, dependsOn: "i3", position: 3, milestone: "i7" }),
    I({ id: "i5", phase: "ph2", title: "Schedule 14 stakeholder interviews", type: "task", status: "doing", priority: "urgent", due: today(), syncEnabled: true, position: 0, milestone: "i7", todoistId: "demo-t5" }),
    // Todoist subtasks of i5 (todoistParentId is a Todoist id), one level nested.
    I({ id: "i5a", phase: "ph2", title: "Email the CFO's assistant for times", type: "task", status: "done", priority: "medium", position: 0, todoistId: "demo-t5a", todoistParentId: "demo-t5" }),
    I({ id: "i5b", phase: "ph2", title: "Book rooms for the clinical leads", type: "task", status: "todo", priority: "medium", start: soon(1), position: 1, todoistId: "demo-t5b", todoistParentId: "demo-t5" }),
    I({ id: "i5c", phase: "ph2", title: "Confirm the video link for remote sites", type: "task", status: "todo", priority: "medium", position: 0, todoistId: "demo-t5c", todoistParentId: "demo-t5b" }),
    I({ id: "i6", phase: "ph2", title: "Department data request (Operations)", type: "task", status: "blocked", priority: "urgent", due: ago(6), syncEnabled: true, position: 1 }),
    I({ id: "i7", phase: "ph2", title: "Synthesis workshop", type: "milestone", status: "todo", priority: "high", due: soon(9), clientVisible: true, position: 2 }),
    I({ id: "i8", phase: "ph3", title: "Draft strategic priorities", type: "deliverable", status: "todo", priority: "high", due: soon(18), clientVisible: true, position: 0, milestone: "i9" }),
    I({ id: "i9", phase: "ph3", title: "Board presentation", type: "milestone", status: "todo", priority: "urgent", due: soon(46), clientVisible: true, position: 1 }),
    I({ id: "i10", phase: "ph4", title: "Collect department org charts", type: "task", status: "done", priority: "medium", due: ago(20), position: 0 }),
    I({ id: "i11", phase: "ph4", title: "Staffing & cost data pull", type: "task", status: "waiting", priority: "high", due: soon(2), position: 1 }),
    I({ id: "i12", phase: "ph5", title: "Costed recommendations draft", type: "deliverable", status: "todo", priority: "high", due: soon(25), clientVisible: true, position: 0 }),
    I({ id: "in1", phase: "inboxph", title: "Follow up on the Cedar County data request", type: "task", status: "todo", priority: "high", due: today(), position: 0 }),
    I({ id: "in2", phase: "inboxph", title: "Book travel for board presentation", type: "task", status: "todo", priority: "medium", due: soon(40), position: 1 }),
  ];

  return { users: [user], folders, projects, phases, items, subtasks: seedSubtasks(), contacts: seedContacts(), artifacts: seedArtifacts(), notes: seedNotes(), clients: seedClients(), time_entries: seedTimeEntries(), active_timer: [], sync_state: [], ...seedPlanner(), ...seedExpenses(), ...seedWorkBlocks() };
}

/**
 * The planner's sample: relative to the current month, so the demo always
 * opens on a populated grid — a retainer with a raise in January, a short
 * fixed-fee job billed unevenly, and one potential bid.
 */
function seedPlanner(): DB {
  const d = new Date();
  const key = (n: number) => {
    const i = d.getFullYear() * 12 + d.getMonth() + n;
    return `${Math.floor(i / 12)}-${String((i % 12) + 1).padStart(2, "0")}`;
  };
  const jan = `${d.getFullYear() + 1}-01`;
  const E = (o: Rec): Rec => ({ user: "u1", created: now(), updated: now(), ...o });
  return {
    plan_entries: [
      E({ id: "pe1", name: "Riverside strategy retainer", kind: "retainer", status: "confirmed", start: key(-2), end: key(14), position: 0,
        segments: [{ from: key(-2), monthlyAmount: 10000, hoursPerWeek: 8 }, { from: jan, monthlyAmount: 12000, hoursPerWeek: 8 }] }),
      E({ id: "pe2", name: "Cedar County ops assessment", kind: "fixed", status: "confirmed", start: key(0), end: key(3), position: 1,
        segments: [{ from: key(0), monthlyAmount: 15000, hoursPerWeek: 12 }, { from: key(1), monthlyAmount: 10000, hoursPerWeek: 12 }, { from: key(3), monthlyAmount: 5000, hoursPerWeek: 6 }] }),
      E({ id: "pe3", name: "Northwind service-line launch", kind: "hourly", status: "potential", start: key(2), end: key(9), position: 2,
        segments: [{ from: key(2), hourlyRate: 225, hoursPerWeek: 10 }] }),
    ],
    plan_settings: [E({ id: "ps1", capacity: [{ from: key(-2), value: 30 }], goals: { [String(d.getFullYear())]: 240000 } })],
  };
}

/**
 * Standing work blocks (Planner › Week ahead): two projects' regular time and a
 * routine block, like the sheet they replace. No settings row, so the
 * defaults apply — the same as a fresh live account.
 */
function seedWorkBlocks(): DB {
  const B = (o: Rec): Rec => ({ user: "u1", enabled: true, days: [], week: "", finishBy: "", itemIds: [], title: "", created: now(), updated: now(), ...o });
  return {
    work_blocks: [
      B({ id: "wb1", project: "p1", purpose: "project", durationMin: 60, perWeek: 3, preferredDay: "tue", position: 0 }),
      B({ id: "wb2", project: "p2", purpose: "project", durationMin: 90, perWeek: 2, latest: "14:00", position: 1 }),
      B({ id: "wb3", title: "Email & admin", purpose: "routine", durationMin: 30, perWeek: 5, preferredTime: "08:00", position: 2 }),
    ],
    work_block_settings: [],
  };
}

/**
 * Clients carry the rates. `Project.client` is free text and is matched to
 * these by name — see effectiveRate() in hooks/useTime.ts — so these names must
 * stay in step with the `client` field on the seeded projects above.
 */
function seedClients(): Rec[] {
  return [
    { id: "cl1", user: "u1", name: "Riverside Health System", defaultRate: 185, currency: "USD", notes: "Retainer renews in January.", created: now(), updated: now() },
    { id: "cl2", user: "u1", name: "Cedar County", defaultRate: 150, currency: "USD", notes: "Public sector rate. Invoice net-45.", reimbursementNotes: "Submit with the county travel form to accountspayable@cedarcounty.gov. Meals over $75 need an itemized receipt.", created: now(), updated: now() },
    { id: "cl3", user: "u1", name: "Northwind Clinic", defaultRate: 165, currency: "USD", notes: "", created: now(), updated: now() },
  ];
}

/**
 * Enough logged time for the totals, the by-project and by-client breakdowns
 * and the day grouping to all have something to show. Includes the two cases
 * that are easy to get wrong: time attached straight to a project rather than a
 * task, and a non-billable entry that must not reach the amount column.
 */
function seedTimeEntries(): Rec[] {
  const at = (daysAgo: number, hour: number, mins: number): string => {
    const d = new Date(Date.now() - daysAgo * 86400000);
    d.setHours(hour, mins, 0, 0);
    return d.toISOString();
  };
  // The client is resolved at creation time in the real app (effectiveRate),
  // so the seed has to do the same — leaving it null makes the by-client
  // breakdown silently empty.
  const CLIENT_OF: Record<string, string> = { p1: "cl1", p1b: "cl1", p2: "cl2", p3: "cl3" };
  const entry = (
    id: string, daysAgo: number, hour: number, durMins: number,
    project: string, item: string | null, note: string,
    billable = true, rate: number | null = 185,
  ): Rec => ({
    id, user: "u1", project, item, client: CLIENT_OF[project] ?? null,
    start: at(daysAgo, hour, 0), end: at(daysAgo, hour + Math.floor(durMins / 60), durMins % 60),
    durationSeconds: durMins * 60, note, billable,
    rateSnapshot: billable ? rate : null, currency: "USD", source: "timer",
    orphaned: false, titleSnapshot: "", created: now(), updated: now(),
  });

  return [
    entry("te1", 0, 9, 150, "p1", "i2", "Payer mix and demographic scan"),
    entry("te2", 0, 13, 90, "p1", null, "Weekly client check-in"),
    entry("te3", 1, 10, 195, "p1", "i3", "Drafting the discovery findings memo"),
    entry("te4", 1, 15, 45, "p1", null, "Internal planning", false, null),
    entry("te5", 2, 9, 270, "p2", null, "Budget and staffing data pull", true, 150),
    entry("te6", 3, 11, 120, "p2", null, "Public works interview", true, 150),
    entry("te7", 4, 14, 90, "p3", null, "Positioning workshop prep", true, 165),
    // Hourly work done as a courtesy, and two kinds of Internal time: plain
    // admin with no project, and a proposal kept on the prospect's project.
    { ...entry("te8", 2, 16, 30, "p2", null, "Quick follow-up call (courtesy)", false, null) },
    { ...entry("te9", 1, 8, 45, "p2", null, "Bookkeeping and invoicing", false, null), project: null, client: null, category: "Admin", internal: true },
    { ...entry("te10", 3, 15, 120, "p1b", null, "CHNA proposal draft", false, null), category: "Proposal", internal: true },
  ];
}

/**
 * The Reimburse app's collections: two open reports and one invoiced, with a
 * missing receipt, a round-trip mileage entry and something still to record in
 * Quicken, so every part of the Expenses section has something to show. The
 * one receipt is an inline SVG — the demo has no file store.
 */
function seedExpenses(): DB {
  const E = (o: Rec): Rec => ({ user: "u1", created: now(), updated: now(), ...o });
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="300" height="420"><rect width="300" height="420" fill="#fff"/><text x="24" y="48" font-family="monospace" font-size="18">HYATT REGENCY</text><text x="24" y="90" font-family="monospace" font-size="14">2 nights  $486.00</text><text x="24" y="380" font-family="monospace" font-size="14">THANK YOU</text></svg>`;
  const receipt = "data:image/svg+xml;base64," + btoa(svg);
  return {
    expense_categories: [
      E({ id: "xc1", name: "Airfare", icon: "plane", sort: 1 }),
      E({ id: "xc2", name: "Train", icon: "train", sort: 2 }),
      E({ id: "xc3", name: "Lodging", icon: "bed", sort: 3 }),
      E({ id: "xc4", name: "Meals", icon: "meal", sort: 4 }),
      E({ id: "xc5", name: "Ground transport", icon: "car", sort: 5 }),
      E({ id: "xc6", name: "Mileage", icon: "road", sort: 6, is_mileage: true }),
      E({ id: "xc7", name: "Parking", icon: "parking", sort: 7 }),
      E({ id: "xc8", name: "Supplies", icon: "box", sort: 8 }),
      E({ id: "xc9", name: "Other", icon: "dots", sort: 9 }),
    ],
    mileage_rates: [
      E({ id: "mr1", effective_date: "2026-07-01", rate: 0.76, note: "IRS business rate \u2014 Jul 2026" }),
      E({ id: "mr2", effective_date: "2026-01-01", rate: 0.725, note: "IRS business rate \u2014 Jan 2026" }),
    ],
    expense_reports: [
      E({ id: "xr1", name: "Riverside board retreat", client: "cl1", project: "p1", type: "trip", status: "active", start_date: ago(12), end_date: ago(9) }),
      E({ id: "xr2", name: "Cedar County site visits", client: "cl2", project: "p2", type: "month", status: "active", start_date: ago(25), end_date: soon(5) }),
      E({ id: "xr3", name: "Northwind kickoff", client: "cl3", project: "p3", type: "trip", status: "archived", start_date: ago(60), end_date: ago(58), invoice_number: "INV-1042", invoiced_at: ago(40) }),
    ],
    expenses: [
      E({ id: "xe1", report: "xr1", category: "xc1", date: ago(12), description: "Alaska \u2014 PDX round trip", amount: 412, receipt_captured: true, reimbursable: true }),
      E({ id: "xe2", report: "xr1", category: "xc3", date: ago(12), description: "Hyatt Regency \u2014 2 nights", amount: 486, receipt_captured: true, reimbursable: true, receipts: ["hyatt_folio.svg"], receiptData: { "hyatt_folio.svg": receipt } }),
      E({ id: "xe3", report: "xr1", category: "xc4", date: ago(11), description: "Working dinner with board chair", amount: 88.4, receipt_captured: false, reimbursable: true }),
      E({ id: "xe4", report: "xr1", category: "xc5", date: ago(10), description: "Taxi to retreat venue", amount: 24, receipt_captured: true, reimbursable: true, added_to_quicken: true }),
      E({ id: "xe5", report: "xr2", category: "xc6", date: ago(20), description: "Home \u2192 Cedar County admin (round trip)", from_location: "Home", to_location: "Cedar County admin", round_trip: true, miles: 142, mileage_rate: 0.76, amount: 107.92, is_mileage: true, reimbursable: true, notes: "Quarterly records review" }),
      E({ id: "xe6", report: "xr2", category: "xc6", date: ago(6), description: "Home \u2192 Public works yard", from_location: "Home", to_location: "Public works yard", round_trip: false, miles: 38.5, mileage_rate: 0.76, amount: 29.26, is_mileage: true, reimbursable: true }),
      E({ id: "xe7", report: "xr2", category: "xc7", date: ago(6), description: "County garage", amount: 12, receipt_captured: true, reimbursable: true }),
      // Bought now for a later visit: kept on the report, not billed yet.
      E({ id: "xe10", report: "xr2", category: "xc1", date: ago(3), description: "Alaska \u2014 flight for the November site visit", amount: 286, receipt_captured: true, reimbursable: false }),
      E({ id: "xe8", report: "xr3", category: "xc1", date: ago(60), description: "United \u2014 ORD", amount: 388.1, receipt_captured: true, reimbursable: true, added_to_quicken: true, added_to_invoice: true }),
      E({ id: "xe9", report: "xr3", category: "xc3", date: ago(60), description: "Marriott \u2014 2 nights", amount: 454, receipt_captured: true, reimbursable: true, added_to_quicken: true, added_to_invoice: true }),
    ],
  };
}

function seedContacts(): Rec[] {
  return [
    { id: "c1", project: "p1", name: "Dana Whitfield", role: "VP Strategy, Riverside", email: "dwhitfield@riverside.org", position: 0, created: now(), updated: now() },
    { id: "c2", project: "p1", name: "Marcus Lee", role: "Board Chair", email: "mlee@riverside.org", position: 1, created: now(), updated: now() },
    { id: "c3", project: "p2", name: "Priya Nair", role: "County Administrator", email: "pnair@cedarcounty.gov", position: 0, created: now(), updated: now() },
  ];
}
function seedArtifacts(): Rec[] {
  // A tiny inline text file to demonstrate stored-file upload + download in the
  // demo (LIVE mode uses a real PocketBase file field instead).
  // btoa() only accepts Latin-1; the em-dash below is U+2014, so encode to
  // UTF-8 bytes first or the whole demo boot throws.
  const sampleText = "Riverside — discovery notes\n\nKey themes: access, affordability, workforce.";
  const sampleFile =
    "data:text/plain;base64," + btoa(String.fromCharCode(...new TextEncoder().encode(sampleText)));
  return [
    { id: "a1", project: "p1", label: "Prior strategic plan (2023)", kind: "PDF", position: 0, created: now(), updated: now() },
    { id: "a2", project: "p1", label: "KPI baseline workbook", kind: "Sheet", position: 1, created: now(), updated: now() },
    { id: "a3", project: "p1", label: "Stakeholder interview guide", kind: "Doc", position: 2, created: now(), updated: now() },
    { id: "a4", project: "p1", label: "Discovery notes.txt", kind: "File", fileName: "discovery-notes.txt", fileData: sampleFile, position: 3, created: now(), updated: now() },
  ];
}
function seedNotes(): Rec[] {
  return [
    { id: "n1", project: "p1", body: "Board wants the refresh framed around access + affordability, not just growth. Reflect in the priorities draft.", pinned: true, created: now(), updated: now() },
    { id: "n2", project: "p1", body: "Ops team slow to return the data request — flagged as blocked. Escalate at Thursday check-in.", pinned: false, created: now(), updated: now() },
  ];
}

function seedSubtasks(): Rec[] {
  const S = (id: string, item: string, title: string, done: boolean, position: number, note = ""): Rec =>
    ({ id, item, title, done, position, note, created: now(), updated: now() });
  return [
    S("s1", "i5", "Draft interview guide", true, 0, "Reuse last year's template; add two questions on access."),
    S("s2", "i5", "Build the stakeholder list", true, 1),
    S("s3", "i5", "Send calendar invites", false, 2, "Aim for 30-minute slots the week of the 24th."),
    S("s4", "i4", "Outline the memo", false, 0),
    S("s5", "i4", "Pull supporting charts", false, 1),
    S("s6", "i8", "Synthesize themes from interviews", false, 0),
  ];
}

function load(): DB {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) {
      const db = JSON.parse(raw);
      // A demo saved before the planner existed gets its sample added, not a
      // blank page — without disturbing anything already in that demo.
      if (!db.plan_entries) Object.assign(db, seedPlanner());
      // Likewise the expense sample, for a demo saved before Expenses existed.
      if (!db.expense_reports) Object.assign(db, seedExpenses());
      if (!db.work_blocks) Object.assign(db, seedWorkBlocks());
      return db;
    }
  } catch {}
  const db = seed();
  save(db);
  return db;
}
function save(db: DB) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(db)); } catch {}
}

// Resolve a dotted path like "phase.project" to a comparable value.
function resolvePath(db: DB, rec: Rec, path: string): any {
  const parts = path.split(".");
  let cur: Rec | undefined = rec;
  for (let i = 0; i < parts.length; i++) {
    if (!cur) return undefined;
    if (i === parts.length - 1) return cur[parts[i]];
    const coll = FIELD_TO_COLLECTION[parts[i]];
    const relId: string = cur[parts[i]];
    cur = coll ? db[coll]?.find((r: Rec) => r.id === relId) : undefined;
  }
  return undefined;
}

function matchFilter(db: DB, rec: Rec, filter?: string): boolean {
  if (!filter) return true;
  // Supports the "path = \"value\"" forms the app uses.
  const m = filter.match(/^\s*([\w.]+)\s*=\s*"([^"]*)"\s*$/);
  if (!m) return true;
  return resolvePath(db, rec, m[1]) === m[2];
}

function expandRec(db: DB, rec: Rec, expand?: string): Rec {
  if (!expand) return { ...rec };
  const out: Rec = { ...rec, expand: { ...(rec.expand || {}) } };
  for (const path of expand.split(",").map((s) => s.trim())) {
    const parts = path.split(".");
    let host = out;
    for (let i = 0; i < parts.length; i++) {
      const field = parts[i];
      const coll = FIELD_TO_COLLECTION[field];
      const related = coll ? db[coll]?.find((r) => r.id === host[field]) : undefined;
      if (!related) break;
      host.expand = host.expand || {};
      host.expand[field] = host.expand[field] || { ...related, expand: {} };
      host = host.expand[field];
    }
  }
  return out;
}

function sortRecs(recs: Rec[], sort?: string): Rec[] {
  if (!sort) return recs;
  const keys = sort.split(",").map((s) => s.trim()).filter(Boolean);
  return [...recs].sort((a, b) => {
    for (const raw of keys) {
      const desc = raw.startsWith("-");
      const key = desc ? raw.slice(1) : raw;
      let av = a[key], bv = b[key];
      if (av == null || av === "") av = undefined;
      if (bv == null || bv === "") bv = undefined;
      if (av === bv) continue;
      if (av === undefined) return 1;
      if (bv === undefined) return -1;
      let cmp = typeof av === "number" && typeof bv === "number" ? av - bv : String(av).localeCompare(String(bv));
      if (cmp !== 0) return desc ? -cmp : cmp;
    }
    return 0;
  });
}

class Collection {
  constructor(private db: DB, private name: string, private persist: () => void) {}
  private list(): Rec[] { return (this.db[this.name] = this.db[this.name] || []); }

  async getFullList<T = Rec>(opts: { filter?: string; sort?: string; expand?: string } = {}): Promise<T[]> {
    const filtered = this.list().filter((r) => matchFilter(this.db, r, opts.filter));
    return sortRecs(filtered, opts.sort).map((r) => expandRec(this.db, r, opts.expand)) as T[];
  }
  async getOne<T = Rec>(id: string, opts: { expand?: string } = {}): Promise<T> {
    const rec = this.list().find((r) => r.id === id);
    if (!rec) throw new Error("not found");
    return expandRec(this.db, rec, opts.expand) as T;
  }
  async getFirstListItem<T = Rec>(filter: string): Promise<T> {
    const rec = this.list().find((r) => matchFilter(this.db, r, filter));
    if (!rec) throw new Error("not found");
    return { ...rec } as T;
  }
  async create<T = Rec>(data: Rec): Promise<T> {
    const rec = { id: uid(this.name.slice(0, 2)), created: now(), updated: now(), ...data };
    delete (rec as any).passwordConfirm;
    this.list().push(rec);
    this.persist();
    return { ...rec } as T;
  }
  async update<T = Rec>(id: string, patch: Rec): Promise<T> {
    const rec = this.list().find((r) => r.id === id);
    if (!rec) throw new Error("not found");
    Object.assign(rec, patch, { updated: now() });
    this.persist();
    return { ...rec } as T;
  }
  async delete(id: string): Promise<boolean> {
    const arr = this.list();
    const idx = arr.findIndex((r) => r.id === id);
    if (idx >= 0) { arr.splice(idx, 1); this.persist(); }
    return true;
  }
  async authWithPassword<T = Rec>(email: string): Promise<{ record: T }> {
    const user = this.list().find((r) => r.email === email) || this.list()[0];
    (globalThis as any).__pbAuth?.set(user);
    return { record: user as T };
  }
}

class AuthStore {
  model: Rec | null;
  token = "demo-token";
  private cbs: Array<() => void> = [];
  constructor(user: Rec | null) { this.model = user; }
  get isValid() { return !!this.model; }
  get record() { return this.model; }
  set(user: Rec | null) { this.model = user; this.cbs.forEach((c) => c()); }
  clear() { this.set(null); }
  save(_token: string, model: Rec) { this.set(model); }
  onChange(cb: () => void) { this.cbs.push(cb); return () => { this.cbs = this.cbs.filter((c) => c !== cb); }; }
}

export function makeMockPb() {
  const db = load();
  const persist = () => save(db);
  const authStore = new AuthStore(db.users[0] || null); // auto-signed-in for the demo
  (globalThis as any).__pbAuth = authStore;
  (globalThis as any).__resetProjectHubDemo = () => { localStorage.removeItem(LS_KEY); location.reload(); };

  return {
    authStore,
    autoCancellation() {},
    collection(name: string) { return new Collection(db, name, persist); },
  };
}

export function resetDemo() {
  localStorage.removeItem(LS_KEY);
  location.reload();
}
